import { useContext, memo } from 'react';
import { Flyer } from '@/graphql/types';
import { cx } from '@emotion/css';
import { Chip, FavoriteIcon } from '@/components';
import { useNavigate } from 'react-router-dom';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import { useAnalytics } from '@/hooks/useAnalytics';
import getSelectedLanguage from '@/utils/getSelectedLanguage';
import DateFnsUtils from '@date-io/date-fns';
import * as dateFnsLocale from 'date-fns/locale';
import styles from './FlyerCard.styles';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import { districtVar } from '@/graphql/state';
import { useReactiveVar } from '@apollo/client';

interface FlyerCardProps {
  trackId?: string;
  flyer: Flyer;
}

const FlyerCard: React.FC<FlyerCardProps> = ({ trackId, flyer }) => {
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const district = useReactiveVar(districtVar);
  const { language } = useContext(UserPreferencesContext);
  const selectedLanguage = getSelectedLanguage(language.code);
  const dateFns = new DateFnsUtils({ locale: dateFnsLocale[selectedLanguage] });
  const {
    flyerId,
    title,
    criticalDate,
    frontImageUrl,
    eventComplement,
    startDate,
    endDate,
    grades,
    isFavorite,
  } = flyer;

  const goToFlyerDetails = (flyerId: number, flyerTitle: string) => {
    track({
      category: `${trackId}:open_flyer_details`,
      action: GenericTrackTypes.LINK_PRESSED,
      label: flyerTitle,
      value: flyerId,
    });
    navigate(`/district/${district?.districtId}/flyer/${flyerId}`);
  };

  const formatEventTime = (date: number) => {
    const newDate = new Date(date);
    const hour = dateFns.formatByString(newDate, 'H');
    if (hour === '0') return null;
    let minute = dateFns.formatByString(newDate, ':m a');
    if (minute.startsWith(':0')) {
      minute = minute.replace(':0', '');
    }

    return `${hour}${minute}`;
  };

  const getEventTime = (startDate: number, endDate: number) => {
    const startDateFormat = formatEventTime(startDate);
    const endDateFormat = formatEventTime(endDate);
    if (startDateFormat && endDateFormat) return `${startDateFormat} - ${endDateFormat}`;
    return '';
  };

  return (
    <div
      className={cx(styles.card, criticalDate === 'Deadline' && styles.deadlineBorder)}
      onClick={() => goToFlyerDetails(flyerId, title)}
      key={flyerId}
      data-testid="flyerCard"
    >
      <div>
        <img
          src={frontImageUrl}
          height="60px"
          width="43px"
          alt={title}
          className={styles.flyerImage}
          data-testid="flyerCardThumbnail"
        />
      </div>
      <div className={styles.cardInformartions}>
        <div className={styles.cardBody}>
          <div>
            <span className={styles.title} data-testid="flyerCardTitle">
              {title}
            </span>
          </div>
          {eventComplement && (
            <div>
              <span className={styles.textGray} data-testid="flyerCardEventComplement">
                {eventComplement}
              </span>
            </div>
          )}
          {criticalDate && (
            <div>
              <Chip
                variant={criticalDate === 'Deadline' ? 'rose' : 'default'}
                dataTestId="flyerCardCriticalDate"
              >
                {criticalDate}
              </Chip>
            </div>
          )}
          <div>
            <span className={styles.textGray} data-testid="flyerCardEventTime">
              {getEventTime(startDate, endDate)}
            </span>
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.grades}>
            {grades.map(grade => (
              <span className={styles.grade} key={grade} data-testid="flyerCardGrade">
                GR {grade}
              </span>
            ))}
          </div>
          <div className={styles.favoriteIconContainer}>
            <FavoriteIcon isFavorite={isFavorite} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(FlyerCard);
